import React, { useState } from 'react';
import { Plus, Trash2, Edit2, Check, X, ChevronDown, ChevronUp } from 'lucide-react';

const MATERIAL_OPTIONS = [
  { value: 'MDF_BRANCO', label: 'MDF Branco' },
  { value: 'MDF_CINZA', label: 'MDF Cinza' },
  { value: 'MDF_SPECIAL', label: 'MDF Especial' },
  { value: 'MDP', label: 'MDP' }
];

const ENVIRONMENT_OPTIONS = [
  { value: 'DORMITORIO', label: 'Dormitório' },
  { value: 'COZINHA', label: 'Cozinha' },
  { value: 'SALA', label: 'Sala' },
  { value: 'BANHEIRO', label: 'Banheiro' },
  { value: 'ESCRITORIO', label: 'Escritório' },
  { value: 'LAVANDERIA', label: 'Lavanderia' },
  { value: 'CLOSET', label: 'Closet' },
  { value: 'GARAGEM', label: 'Garagem' }
];

const generatePartCode = (index) => {
  return `P${String(index + 1).padStart(3, '0')}`;
};

const ManualPartBuilder = ({ parts = [], onPartsChange, selectedMaterial }) => {
  const [editingId, setEditingId] = useState(null);
  const [editingPart, setEditingPart] = useState(null);
  const [sortField, setSortField] = useState('partCode');
  const [sortDirection, setSortDirection] = useState('asc');

  const defaultPart = {
    partCode: '',
    partName: '',
    width: '',
    height: '',
    thickness: selectedMaterial?.espessura_mm || '18',
    quantity: 1,
    materialName: selectedMaterial?.nome || 'MDF Branco',
    materialCategory: selectedMaterial?.categoria || 'MDF_BRANCO',
    environment: '',
    description: '',
    edgeBandTop: 0,
    edgeBandBottom: 0,
    edgeBandLeft: 0,
    edgeBandRight: 0
  };

  const handleAddPart = () => {
    const newPart = {
      ...defaultPart,
      partCode: generatePartCode(parts.length)
    };
    onPartsChange([...parts, newPart]);
    setEditingId(newPart.partCode);
    setEditingPart({ ...newPart });
  };

  const handleEditPart = (part) => {
    setEditingId(part.partCode);
    setEditingPart({ ...part });
  };

  const handleSaveEdit = () => {
    if (!editingPart.partCode || !editingPart.partName || !editingPart.width || !editingPart.height) {
      return;
    }

    const updatedParts = parts.map(p =>
      p.partCode === editingId ? editingPart : p
    );
    onPartsChange(updatedParts);
    setEditingId(null);
    setEditingPart(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingPart(null);
  };

  const handleDeletePart = (partCode) => {
    const updatedParts = parts.filter(p => p.partCode !== partCode);
    onPartsChange(updatedParts);
  };

  const handleFieldChange = (field, value) => {
    setEditingPart(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 inline ml-1" />
      : <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  const totalArea = parts.reduce((sum, p) => {
    const area = (parseFloat(p.width) || 0) * (parseFloat(p.height) || 0) * (parseInt(p.quantity) || 1);
    return sum + area;
  }, 0) / 1000000;

  const totalQuantity = parts.reduce((sum, p) => sum + (parseInt(p.quantity) || 1), 0);

  const uniqueMaterials = [...new Set(parts.map(p => p.materialName).filter(Boolean))];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-medium">{parts.length}</span> peça(s) |{' '}
          <span className="font-medium">{totalQuantity}</span> unidades |{' '}
          <span className="font-medium">{totalArea.toFixed(3)}</span> m² |{' '}
          <span className="font-medium">{uniqueMaterials.length}</span> material(is)
        </div>
        <button
          onClick={handleAddPart}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          Adicionar Peça
        </button>
      </div>

      {parts.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-500 mb-4">Nenhuma peça adicionada ainda</p>
          <button
            onClick={handleAddPart}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Adicionar primeira peça
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('partCode')}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                >
                  Código <SortIcon field="partCode" />
                </th>
                <th
                  onClick={() => handleSort('partName')}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                >
                  Nome <SortIcon field="partName" />
                </th>
                <th
                  onClick={() => handleSort('width')}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                >
                  Comp. <SortIcon field="width" />
                </th>
                <th
                  onClick={() => handleSort('height')}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                >
                  Larg. <SortIcon field="height" />
                </th>
                <th
                  onClick={() => handleSort('thickness')}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                >
                  Esp. <SortIcon field="thickness" />
                </th>
                <th
                  onClick={() => handleSort('quantity')}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                >
                  Qtd <SortIcon field="quantity" />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Material
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Ambiente
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parts.map((part) => (
                <tr key={part.partCode} className="hover:bg-gray-50">
                  {editingId === part.partCode ? (
                    <>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={editingPart.partCode}
                          onChange={(e) => handleFieldChange('partCode', e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={editingPart.partName}
                          onChange={(e) => handleFieldChange('partName', e.target.value)}
                          className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={editingPart.width}
                          onChange={(e) => handleFieldChange('width', e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={editingPart.height}
                          onChange={(e) => handleFieldChange('height', e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={editingPart.thickness}
                          onChange={(e) => handleFieldChange('thickness', e.target.value)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="1"
                          value={editingPart.quantity}
                          onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value) || 1)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={editingPart.materialCategory}
                          onChange={(e) => {
                            const mat = MATERIAL_OPTIONS.find(m => m.value === e.target.value);
                            handleFieldChange('materialCategory', e.target.value);
                            handleFieldChange('materialName', mat?.label || e.target.value);
                          }}
                          className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          {MATERIAL_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={editingPart.environment}
                          onChange={(e) => handleFieldChange('environment', e.target.value)}
                          className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="">-</option>
                          {ENVIRONMENT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={handleSaveEdit}
                          className="p-1 text-green-600 hover:text-green-800"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 text-red-600 hover:text-red-800 ml-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-medium text-gray-900 text-sm">{part.partCode}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-gray-700 text-sm">{part.partName}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-gray-700 text-sm">{part.width}mm</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-gray-700 text-sm">{part.height}mm</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-gray-700 text-sm">{part.thickness}mm</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-gray-700 text-sm">x{part.quantity}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-gray-600 text-sm">{part.materialName}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-gray-500 text-sm">
                          {ENVIRONMENT_OPTIONS.find(e => e.value === part.environment)?.label || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => handleEditPart(part)}
                          className="p-1 text-blue-600 hover:text-blue-800"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePart(part.partCode)}
                          className="p-1 text-red-600 hover:text-red-800 ml-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {parts.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Resumo das Peças</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Total de Peças:</span>
              <span className="ml-2 font-medium">{parts.length}</span>
            </div>
            <div>
              <span className="text-gray-500">Total de Unidades:</span>
              <span className="ml-2 font-medium">{totalQuantity}</span>
            </div>
            <div>
              <span className="text-gray-500">Área Total:</span>
              <span className="ml-2 font-medium">{totalArea.toFixed(3)} m²</span>
            </div>
            <div>
              <span className="text-gray-500">Materiais:</span>
              <span className="ml-2 font-medium">{uniqueMaterials.length}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualPartBuilder;
